<template>
  <b-modal ref="homfaxModal" v-on:show="checkEdit" v-on:hide="resetValues" size="lg" hide-footer centered>
    <loading v-bind:active.sync="loading"></loading>
    <div class="container">
      <div class="row">
        <div class="col-md-4">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fas fa-dollar-sign"></i></span>
            </div>
            <money v-bind="moneyProperties"
                   v-bind:class="{'is-invalid': costInvalid}"
                   v-model="cost"
                   class="form-control form-control-md square-left"></money>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
            <service-provider-search class="input-group" v-bind:stylingClasses="'form-control form-control-md square-left'" v-bind:initialSearchTerm="serviceProviderName" v-bind:placeholder="'Merchant Search'" v-on:suggestionSelected="updateServiceProvider"></service-provider-search>
        </div>
      </div>
      <div class="row" v-if="!edit">
        <div class="col-md-4">
          <date-picker v-model="date">
            <div class="input-group" slot-scope="{ inputValue, updateValue }">
              <div class="input-group-prepend">
                <span class="input-group-text"><i class="fas fa-calendar"></i></span>
              </div>
              <input type="text" class="form-control square-left white-background" v-bind:value="inputValue" v-on:change.native="updateValue($event.target.value)" placeholder="Purchase Date" disabled>
            </div>
          </date-picker>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fa fa-hammer"></i></span>
            </div>
            <select v-model="selectedCategory" v-on:change="getServices(selectedCategory)" class="form-control" v-bind:class="{'is-invalid': categoryInvalid}">
              <option disabled>Service</option>
              <option v-for="category in categories" v-bind:key="category.id">{{ category }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text"><i class="fa fa-hammer"></i></span>
            </div>
            <select v-model="selectedService" class="form-control" v-bind:class="{'is-invalid': serviceInvalid, 'disabled': !selectedCategory}">
              <option disabled>Type</option>
              <option v-for="service in services" v-bind:key="service.id">{{ service }}</option>
            </select>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <p class="switch-label">Contractor</p>
          <label class="switch">
            <input v-model="diy" type="checkbox">
            <span class="slider round"></span>
          </label>
          <p class="switch-label">DIY</p>
        </div>
      </div>
      <div class="row">
        <div class="col-lg-2">
          <div class="input-group no-margin">
            <div class="input-group-prepend">
              <span class="input-group-text note-icon"><i class="fa fa-sticky-note"></i></span>
            </div>
            <p class="form-control note-label">Note</p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col my-auto">
          <div class="input-group">
            <input type="textarea" v-model="note" class="form-control note-input" rows=3 placeholder="Type notes here..."/>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <p class="no-margin">*Total file size must be less than 6MB</p>
        </div>
      </div>
      <div class="row">
        <div class="col input-spacing">
          <label class="custom-file-label mx-3" for="customFile">Upload files*</label>
          <input class="custom-file-input" id="customFile" type="file" multiple accept="image/png, image/jpeg" v-on:change="previewImage">
          <img v-for="(image, index) in attachments" class="preview-image" v-bind:key="index" v-bind:src="signedAttachments[index]" v-on:click="removeImage(index)" height="100px">
        </div>
      </div>
      <div class="row">
        <div class="col-4 offset-2">
          <button class="btn btn-primary" v-on:click="createReceipt()">Save</button>
        </div>
        <div class="col-4">
          <button class="btn btn-secondary" v-on:click="closeModal()">Close</button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { Money } from 'v-money'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import S3 from 'aws-sdk/clients/s3'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'
import ServiceProviderSearch from '../../../general/inputs/GoogleTextSearch.vue'

export default {
  components: {
    DatePicker,
    Loading,
    Money,
    ServiceProviderSearch
  },
  data () {
    return {
      loading: false,
      edit: false,
      categories: null,
      services: null,
      cost: 0,
      date: null,
      selectedCategory: 'Service',
      selectedService: 'Type',
      serviceProviderAddress: null,
      serviceProviderId: null,
      serviceProviderName: null,
      diy: false,
      note: null,
      attachments: [],
      moneyProperties: {
        decimal: '.',
        thousands: ',',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      },
      categoryInvalid: false,
      costInvalid: false,
      serviceInvalid: false
    }
  },
  props: {
    homfax: {
      type: Object,
      required: false
    },
    propertyId: String
  },
  computed: {
    ...mapGetters([
      'getCognitoId'
    ]),
    signedAttachments () {
      let s3 = new S3()
      let signedAttachments = []
      let cognitoId = this.getCognitoId
      this.attachments.forEach((photoName) => {
        if (!photoName.includes(cognitoId)) {
          signedAttachments.push(photoName)
          return
        }
        let params = {
          Bucket: window.CONFIG.HomFaxAttachmentsBucketName,
          Key: photoName,
          Expires: 10
        }
        let signedUrl = s3.getSignedUrl('getObject', params)
        signedAttachments.push(signedUrl)
      })
      return signedAttachments
    }
  },
  methods: {
    updateServiceProvider (datum) {
      console.log('updateServiceProvider')
      if (typeof datum === 'object' && datum !== null) {
        if (datum.formatted_address) this.serviceProviderAddress = datum.formatted_address
        if (datum.place_id) this.serviceProviderId = datum.place_id
        if (datum.name) this.serviceProviderName = datum.name
      } else {
        this.serviceProviderAddress = null
        this.serviceProviderId = null
        this.serviceProviderName = datum
      }
    },
    async checkEdit () {
      // Fill in existing values if editing receipt
      if (!this.homfax) return
      if (this.homfax) this.edit = true
      if (this.homfax.timestamp) this.date = this.homfax.timestamp
      if (this.homfax.price) this.cost = this.homfax.price
      if (this.homfax.category) {
        this.selectedCategory = this.homfax.category
        await this.getServices(this.selectedCategory)
      }
      if (this.homfax.service) this.selectedService = this.homfax.service
      if (this.homfax.service_provider_address) this.serviceProviderAddress = this.homfax.service_provider_address
      if (this.homfax.service_provider_id) this.serviceProviderId = this.homfax.service_provider_id
      if (this.homfax.service_provider_name) this.serviceProviderName = this.homfax.service_provider_name
      if (this.homfax.diy) this.diy = this.homfax.diy
      if (this.homfax.notes) this.note = this.homfax.notes
      if (this.homfax.attachments) {
        this.attachments = []
        this.attachments.push(...this.homfax.attachments)
      }
    },
    closeModal () {
      this.$refs.homfaxModal.hide()
    },
    resetValues () {
      this.$emit('resetSelectedHomfax')
      this.edit = false
      this.services = null
      this.cost = 0
      this.date = null
      this.selectedCategory = 'Service'
      this.selectedService = 'Type'
      this.serviceProviderAddress = null
      this.serviceProviderId = null
      this.serviceProviderName = null
      this.diy = false
      this.note = null
      this.attachments = []
      this.categoryInvalid = false
      this.costInvalid = false
      this.serviceInvalid = false
    },
    async createReceipt () {
      this.loading = true
      try {
        var client = this.$store.getters.getHomFaxApigClientFactory
        let formattedDate = null
        if (typeof this.date === 'object') {
          formattedDate = this.date.toISOString().replace('Z', '')
        } else {
          formattedDate = this.date
        }
        let category = this.selectedCategory
        if (this.selectedCategory === 'Service') category = null
        let service = this.selectedService
        if (this.selectedService === 'Type') service = null
        var body = {
          'timestamp': formattedDate,
          'category': category,
          'service': service,
          'diy': this.diy,
          'price': this.cost,
          'service_provider_address': this.serviceProviderAddress,
          'service_provider_id': this.serviceProviderId,
          'service_provider_name': this.serviceProviderName,
          'notes': this.note,
          'attachments': this.attachments
        }
        var params = {'property_id': this.propertyId}
        var pathTemplate = '/homfax/{property_id}'
        var method = 'POST'
        if (this.edit) method = 'PUT'
        var additionalParams = {}
        await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        this.$emit('newHomfaxReceipt', true)
        this.$refs.homfaxModal.hide()
      } catch (error) {
        console.log(error)
      }
      this.loading = false
    },
    async getCategories () {
      this.loading = true
      var client = this.$store.getters.getBusinessApigClientFactory
      var params = {}
      var pathTemplate = '/businesses/categories'
      var method = 'GET'
      var additionalParams = {}
      var body = {}
      try {
        var result = await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        this.categories = result.data.categories.sort()
      } catch (error) {
        console.log(error)
      }
      this.loading = false
    },
    async getServices (category) {
      this.loading = true
      var client = this.$store.getters.getBusinessApigClientFactory
      var params = {}
      var pathTemplate = '/businesses/services'
      var method = 'POST'
      var additionalParams = {}
      var body = {
        'categories': [category]
      }
      try {
        var result = await client.invokeApi(params, pathTemplate, method, additionalParams, body)
        this.services = result.data.services[category].sort()
      } catch (error) {
        console.log(error)
      }
      this.loading = false
    },
    previewImage: function (event) {
      var input = event.target
      var that = this
      for (var i = 0; i < input.files.length; i++) {
        if (input.files && input.files[i]) {
          var reader = new FileReader()
          reader.onload = function (e) {
            if (!that.attachments.includes(e.target.result)) {
              var img = new Image()
              img.onload = function () {
                if (this.width > 150 && this.height > 150) {
                  that.attachments.push(e.target.result)
                } else {
                  that.imageTooSmall = true
                }
              }
            }
            img.src = e.target.result
          }
          reader.readAsDataURL(input.files[i])
        }
      }

      var fileInput = document.getElementById('customFile')
      fileInput.value = null
    },
    removeImage (index) {
      this.attachments.splice(index, 1)
    }
  },
  watch: {
    homfax () {
      this.checkEdit()
    }
  },
  async created () {
    await this.getCategories()
  }
}
</script>

<style scoped>
.btn {
  width: 100%;
}

.input-group {
  margin-bottom: 0.5rem;
}

.input-spacing {
  margin-bottom: 0.5rem;
}

.no-margin {
  margin: 0;
}

.note-icon {
  border-radius: 0;
  border-top-left-radius: 0.25rem;
  margin-bottom: 0;
}

.note-input {
  border-top-left-radius: 0;
}

.note-label {
  border-radius: 0;
  border-top-right-radius: 0.25rem;
  margin-bottom: 0;
}

.switch-label {
  display: inline;
  vertical-align: middle;
}

.switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  -webkit-transition: .4s;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: .4s;
  transition: .4s;
}

input:checked + .slider {
  background-color: #FEEDAA;
}

input:checked + .slider:before {
  -webkit-transform: translateX(26px);
  -ms-transform: translateX(26px);
  transform: translateX(26px);
}
/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.white-background {
  background-color: #fff;
}
</style>



// WEBPACK FOOTER //
// src/components/home/homeData/rightSide/HomFAXModal.vue